"use client";

import MediaImage from "@/components/ui/MediaImage";

import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisStrokeVertical } from "@fortawesome/pro-light-svg-icons";
import {
  faArrowsMaximize as thinFaArrowsMaximize,
  faImage as thinFaImage,
  faLocationDot as thinFaLocationDot,
  faTableLayout as thinFaTableLayout,
  faVideo as thinFaVideo,
} from "@fortawesome/sharp-light-svg-icons";
import {
  faArrowsMaximize as solidFaArrowsMaximize,
  faImage as solidFaImage,
  faLocationDot as solidFaLocationDot,
  faTableLayout as solidFaTableLayout,
  faVideo as solidFaVideo,
} from "@fortawesome/sharp-solid-svg-icons";
import MapWrapper from "./ui/MapWrapper";
import "swiper/css";
import "swiper/css/navigation";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import Link from "next/link";
import { CaseData } from "@/cms/models";
interface ImageSliderProps {
  caseData: CaseData;
  children?: React.ReactNode;
  labelOverlay?: React.ReactNode;
  isFullscreen: boolean;
  fontType: string;
  setIsFullscreen: (isFullscreen: boolean) => void;
  /** The photos cover the slider whatever its height, which the section's Højde or Fyld skærmhøjden sets. */
  fill?: boolean;
}

const CaseDetailsImageSlider: React.FC<ImageSliderProps> = ({
  caseData,
  children,
  labelOverlay,
  fontType,
  isFullscreen,
  setIsFullscreen,
  fill = false,
}) => {
  const [selectedView, setSelectedView] = useState<string>("photo");
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const sliderRef = useRef<any>(null);
  const handleViewChange = (view: string) => {
    setSelectedView(view);
  };

  const hasDrawing = caseData.images.some((x) =>
    x.namespace.endsWith("drawing"),
  );
  const hasMap = caseData.latitude && caseData.longitude;

  const handleFullscreen = () => {
    if (!isFullscreen) {
      if (sliderRef.current) {
        if (sliderRef.current.requestFullscreen) {
          sliderRef.current.requestFullscreen();
        } else if (sliderRef.current.webkitRequestFullscreen) {
          sliderRef.current.webkitRequestFullscreen();
        } else if (sliderRef.current.mozRequestFullScreen) {
          sliderRef.current.mozRequestFullScreen();
        } else if (sliderRef.current.msRequestFullscreen) {
          sliderRef.current.msRequestFullscreen();
        }
      }
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange,
      );
    };
  }, []);

  return (
    <div
      ref={sliderRef}
      className="relative select-none h-full overflow-hidden custom-slider"
    >
      {selectedView === "photo" && !isFullscreen && labelOverlay}
      {selectedView === "photo" && (
        <div className="absolute hidden lg:block left-20 right-20 z-10 top-20">
          <div className="max-w-screen-2xl flex mx-auto justify-between">
            {children}
          </div>
        </div>
      )}
      {isFullscreen && ["photo", "drawing"].includes(selectedView) && (
        <div className="absolute text-right text-[#000] top-0 left-0 right-0 bg-[#F0F0F0] bg-opacity-90 z-20">
          <div className="max-w-screen-2xl flex mx-auto justify-between">
            <span className="flex items-center p-3">
              {
                caseData.images.filter((x) =>
                  x.namespace.endsWith(selectedView),
                )[activeIndex].description
              }
            </span>
            <span className="p-3">
              {activeIndex}/{caseData.images.length}
            </span>
          </div>
        </div>
      )}
      {isFullscreen && selectedView === "video" && (
        <div className="absolute text-right text-[#000] top-0 left-0 right-0 bg-[#F0F0F0] bg-opacity-90 z-20">
          <div className="max-w-screen-2xl flex mx-auto justify-end">
            <span className="p-3">
              {activeIndex}/{caseData.videoUrls.length}
            </span>
          </div>
        </div>
      )}
      {selectedView === "map" && caseData.latitude && caseData.longitude && (
        <MapWrapper
          markers={[
            {
              longitude: caseData.longitude,
              latitude: caseData.latitude,
            },
          ]}
        />
      )}
      {["photo", "drawing"].includes(selectedView) && (
        <Swiper
          onRealIndexChange={(swiperCore) => {
            setActiveIndex(swiperCore.realIndex);
          }}
          className="select-none"
          navigation={true}
          modules={[Navigation]}
        >
          {caseData.images
            .filter((x) => x.namespace.endsWith(selectedView))
            .map((image, index) => (
              <SwiperSlide key={index}>
                <div
                  className={`${isFullscreen ? "bg-[#3C3C3B] relative items-center justify-center h-full" : "h-full"} relative select-none w-full ${selectedView === "drawing" ? "z-50" : ""} `}
                >
                  <MediaImage
                    src={image.xlUrl}
                    alt={`Slide ${index}`}
                    loading={index < 2 ? "eager" : "lazy"}
                    className={`${isFullscreen ? "!object-contain" : "object-cover"}${fill ? " h-full w-full" : ""}`}
                  />
                </div>
              </SwiperSlide>
            ))}
        </Swiper>
      )}
      {selectedView === "video" && (
        <Swiper
          onRealIndexChange={(swiperCore) => {
            setActiveIndex(swiperCore.realIndex);
          }}
          className="select-none"
          navigation={true}
          modules={[Navigation]}
        >
          {caseData.videoUrls.map((videoUrl, index) => (
            <SwiperSlide key={index}>
              <video
                src={videoUrl}
                controls
                className="w-full h-full object-cover pb-10 md:pb-12"
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
      <div className="absolute text-[#000] bottom-0 left-0 right-0 bg-[#F0F0F0] bg-opacity-90 z-10">
        <div className="max-w-screen-2xl flex mx-auto justify-between">
          <div className="flex">
            <ImageSliderTab
              isActive={selectedView === "photo"}
              onClick={() => handleViewChange("photo")}
            >
              <FontAwesomeIcon
                className="size-4"
                icon={fontType === "modern" ? thinFaImage : solidFaImage}
              />
              <span className="hidden lg:block">Billeder</span>
            </ImageSliderTab>
            {hasDrawing && (
              <ImageSliderTab
                isActive={selectedView === "drawing"}
                onClick={() => handleViewChange("drawing")}
              >
                <FontAwesomeIcon
                  className="size-4"
                  icon={
                    fontType === "modern"
                      ? thinFaTableLayout
                      : solidFaTableLayout
                  }
                />
                <span className="hidden lg:block">Plantegning</span>
              </ImageSliderTab>
            )}
            {caseData.videoUrls?.length > 0 && (
              <ImageSliderTab
                isActive={selectedView === "video"}
                onClick={() => handleViewChange("video")}
              >
                <FontAwesomeIcon
                  className="size-4"
                  icon={fontType === "modern" ? thinFaVideo : solidFaVideo}
                />
                <span className="hidden lg:block">Videos</span>
              </ImageSliderTab>
            )}
            {hasMap && (
              <ImageSliderTab
                isActive={selectedView === "map"}
                onClick={() => handleViewChange("map")}
              >
                <FontAwesomeIcon
                  className="size-4"
                  icon={
                    fontType === "modern"
                      ? thinFaLocationDot
                      : solidFaLocationDot
                  }
                />
                <span className="hidden lg:block">Kort</span>
              </ImageSliderTab>
            )}
            {caseData.videoLinks?.length > 0 && (
              <Listbox>
                <ListboxButton className="text-[#3C3C3B] px-4 py-2">
                  <FontAwesomeIcon
                    className="size-5"
                    icon={faEllipsisStrokeVertical}
                  />
                </ListboxButton>
                <ListboxOptions
                  transition
                  anchor="bottom"
                  className="border shadow-lg bg-[#ecf0f1] py-1"
                >
                  {caseData.videoLinks.map((link) => (
                    <ListboxOption
                      key={link.url}
                      value={link.url}
                      className="data-[focus]:cursor-pointer homepage-text-sm data-[focus]:bg-[var(--select-option-text-color)] data-[focus]:text-white bg-[#ecf0f1] text-[var(--select-option-text-color)] px-4 py-2 "
                    >
                      <Link target={link.url} href={link.url}>
                        {link.displayName}
                      </Link>
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Listbox>
            )}
          </div>
          <div>
            <button
              aria-label="FullScreen"
              className="p-3 flex gap-2 items-center cursor-pointer"
              onClick={() => handleFullscreen()}
            >
              <span className="hidden lg:block homepage-text-sm">
                {isFullscreen ? "Luk fuld skærm" : "Fuld skærm"}
              </span>
              <FontAwesomeIcon
                className="size-4"
                icon={
                  fontType === "modern"
                    ? thinFaArrowsMaximize
                    : solidFaArrowsMaximize
                }
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ImageSliderTab: React.FC<{
  isActive: boolean;
  onClick: () => void;
  children?: React.ReactNode;
  labelOverlay?: React.ReactNode;
}> = ({ isActive, onClick, children }) => {
  return (
    <button
      className={`p-3 flex gap-2 items-center hover:bg-[var(--slider-hover-bg-color)] homepage-text-sm hover:text-[var(--slider-hover-text-color)] cursor-pointer ${
        isActive
          ? "bg-[var(--slider-active-bg-color)] text-[var(--slider-active-text-color)]"
          : "bg-[var(--slider-bg-color)] text-[var(--slider-text-color)]"
      }`}
      onClick={() => onClick()}
    >
      {children}
    </button>
  );
};

export default CaseDetailsImageSlider;
